import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from 'components/Assessments/SocAssessment/types/SocFormData'
import { SocFormField } from 'components/Assessments/SocAssessment/types/SocFormField'
import { SocAssessmentQuestionnaireItem } from 'components/Assessments/SocAssessment/types/questionnaire'

/**
 * Integumentary subsection adapter
 * Handles skin assessment, wounds, and nail condition (6 fields)
 */
export class IntegumentaryAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Integumentary system assessment fields
    const skinColor = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR
    )?.at(0)

    const skinIssues = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES
    )

    const nails = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS
    )?.at(0)

    const identifiedWounds = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS
    )?.at(0)

    const woundNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [SocFormField.INTEGUMENTARY_SKIN_COLOR]: skinColor,
      [SocFormField.INTEGUMENTARY_SKIN_ISSUES]: skinIssues,
      [SocFormField.INTEGUMENTARY_NAILS]: nails,
      [SocFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS]: identifiedWounds,
      [SocFormField.INTEGUMENTARY_WOUND_NOTES]: woundNotes,
      [SocFormField.INTEGUMENTARY_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_COLOR,
        value: this.getFormDataField(SocFormField.INTEGUMENTARY_SKIN_COLOR)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SKIN_ISSUES,
        value: this.getFormDataField(SocFormField.INTEGUMENTARY_SKIN_ISSUES)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_NAILS,
        value: this.getFormDataField(SocFormField.INTEGUMENTARY_NAILS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_IDENTIFIED_WOUNDS,
        value: this.getFormDataField(SocFormField.INTEGUMENTARY_IDENTIFIED_WOUNDS)
      },
      {
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_SYSTEM_SUMMARY,
        value: this.getFormDataField(SocFormField.INTEGUMENTARY_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add wound notes if they exist (they are conditionally rendered when wounds are identified)
    if (this.formDataFieldExists(SocFormField.INTEGUMENTARY_WOUND_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_INTEGUMENTARY_WOUND_NOTES,
        value: this.getFormDataField(SocFormField.INTEGUMENTARY_WOUND_NOTES)
      })
    }

    return mutations
  }
}
